<template>
  <el-container>
    <el-main>
      <el-row :gutter="20" style="display: flex">
        <div>
          <div>
            <el-select
              v-model="selectedMessageTemplate"
              placeholder="Select a message template"
              @change="handleViewMessageTemplateChange"
              filterable
            >
              <el-option
                v-for="item in uniqueMessageTemplates"
                :key="item.id"
                :label="item.name"
                :value="item.name"
              />
            </el-select>
            <el-select
              style="margin-left: 5px"
              ref="availableMessageTemplateLanguagesDropdown"
              v-model="selectedMessageTemplateLanguage"
              placeholder="Select a language"
              @change="handleViewMessageTemplateLanguageChange"
              filterable
            >
              <el-option
                v-for="language in availableMessageTemplateLanguages"
                :key="language"
                :label="language"
                :value="language"
              />
            </el-select>
            <el-button
              style="margin-left: 10px"
              type="primary"
              icon="el-icon-plus"
              @click="addNewTemplate"
              >New Template</el-button
            >
            <el-button
              :disabled="!categoryIsSupported"
              type="primary"
              v-if="selectedMessageTemplate && selectedMessageTemplateLanguage"
              icon="el-icon-plus"
              @click="addNewLanguage"
              >New Language</el-button
            >
            <el-tooltip
              v-if="
                !categoryIsSupported && selectedMessageTemplate && selectedMessageTemplateLanguage
              "
              content="You cannot add a language because this template category no longer exists. Please create a new template instead."
              placement="bottom-start"
            >
              <i class="el-icon-info" style="margin-left: 10px; color: grey" />
            </el-tooltip>
            <el-tooltip
              content="It refreshes message templates from network instead of cache"
              placement="bottom"
              v-if="showAdvanced || showRefreshButton"
            >
              <el-button
                type="primary"
                icon="el-icon-refresh"
                @click="refreshTemplate"
                circle
                plain
                :loading="refreshingMessageTemplate"
              ></el-button>
            </el-tooltip>
            <el-button :disabled="true" plain type="success"
              >Graph API {{ graphAPIVersion }}</el-button
            >
            <el-tooltip
              content="Graph API version can be configured in Settings > Whatsapp > Graph API Version"
              placement="right"
            >
              <i class="el-icon-info" style="margin-left: 10px; color: grey" />
            </el-tooltip>
          </div>
          <div style="position: relative; display: flex; justify-content: center"></div>
        </div>
      </el-row>
      <el-row :gutter="20">
        <div>
          <el-dialog title="Preview" :visible.sync="previewDialogVisible" width="50%" center>
            <div style="position: relative; display: flex; justify-content: center">
              <WhatsAppMessageTemplatePreview
                :header="previewHeader"
                :body="previewBody"
                :footer="previewFooter"
                :buttons="previewButtons"
                :button-type="previewButtonType"
              />
            </div>
          </el-dialog>
          <MessageTemplateForm
            :addingNewLanguage="addingNewLanguage"
            :selectedLanguages="selectedLanguages"
            :name="name"
            :language="language"
            :category="category"
            :parameterFormat="parameter_format"
            :header="header"
            :body="body"
            :footer="footer"
            :buttons="buttons"
            :button-type="buttonType"
            :status="status"
            :headerExamples="headerExamples"
            :bodyExamples="bodyExamples"
            :read-only="readOnly"
            :authProp="authentication"
            ref="form"
          >
            <template #footer="slotProps">
              <el-button
                type="primary"
                :loading="addingMessageTemplate"
                @click="confirm('create', slotProps)"
                v-if="!readOnly"
                :disabled="slotProps.warningExists || !slotProps.sampleCompleted"
                >Add Message Template</el-button
              >
              <el-button
                type="primary"
                plain
                :loading="updatingMessageTemplate"
                :disabled="category === 'AUTHENTICATION'"
                @click="create('update', slotProps)"
                v-if="readOnly"
                >Update Message Template</el-button
              >
              <el-tooltip
                v-if="category === 'AUTHENTICATION'"
                content="Authentication templates cannot be updated."
                placement="bottom-start"
              >
                <i
                  class="el-icon-info"
                  style="margin-left: 10px; margin-right: 16px; color: grey"
                />
              </el-tooltip>
              <el-tooltip
                v-else-if="slotProps.warningExists"
                content="Please check on any field inputs that have warnings before continue."
                placement="bottom-start"
              >
                <i
                  class="el-icon-info"
                  style="margin-left: 10px; margin-right: 16px; color: grey"
                />
              </el-tooltip>
              <el-button
                type="primary"
                :loading="updatingMessageTemplate"
                @click="deleteMessageTemplate(slotProps)"
                v-if="readOnly"
                >Delete Message Template</el-button
              >
              <el-button :loading="previewingTemplate" :disabled="slotProps.category !== 'AUTHENTICATION' && !slotProps.body?.text" type="primary" @click="preview(slotProps)">Preview</el-button>
              <el-tooltip
                v-if="slotProps.category !== 'AUTHENTICATION' && !slotProps.body?.text"
                content="Select a category and fill the body text to enable preview."
                placement="bottom-start"
              >
                <i
                  class="el-icon-info"
                  style="margin-left: 10px; margin-right: 16px; color: grey"
                />
              </el-tooltip>
            </template>
          </MessageTemplateForm>
        </div>
      </el-row>
    </el-main>
  </el-container>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
import WhatsAppMessageTemplatePreview from "./WhatsAppMessageTemplatePreview";
import MessageTemplateForm from "./MessageTemplateForm";
import { messageTemplateCategories } from "./messageTemplateConstant";

/**
 * MessageTemplateTab - WhatsApp message template management interface.
 *
 * @component
 * @description Main component for creating, editing, and managing WhatsApp Business API
 * message templates. Handles template creation, language variants, preview, and submission
 * to WhatsApp for approval. Supports template categories, headers, body, footer, and
 * call-to-action/quick-reply buttons.
 *
 * Features:
 * - Template CRUD operations (create, view, edit, delete)
 * - Multi-language support with language variant management
 * - Real-time template preview with sample data
 * - Template validation and submission to WhatsApp
 * - Template status tracking (pending, approved, rejected)
 * - Category-based templates (marketing, utility, authentication)
 * - Dynamic parameter support for personalization
 *
 * @prop {Array} messageTemplates - List of WhatsApp message templates
 * @prop {String} wa_endpoint - WhatsApp Business API endpoint identifier
 * @fires NONE - Does not emit events
 *
 * @todo REFACTOR (Priority: Medium): This file has 650 lines.
 *   Recommended split:
 *   - components/WhatsApp/MessageTemplate/TemplateSelector.vue (~100 lines) - Template/language dropdown selection
 *   - components/WhatsApp/MessageTemplate/TemplatePreviewDialog.vue (~80 lines) - Preview modal
 *   - components/WhatsApp/MessageTemplate/TemplateActions.vue (~100 lines) - Submit/delete/refresh actions
 *   - components/WhatsApp/MessageTemplate/MessageTemplateTabContainer.vue (~370 lines) - Main orchestration
 *   - helperMethods/whatsapp/messageTemplate.ts (~100 lines) - Template validation and transformation logic
 *
 * @example
 * <MessageTemplateTab :messageTemplates="templates" :wa_endpoint="waId" />
 */
export default {
  name: "MessageTemplateTab",
  components: {
    WhatsAppMessageTemplatePreview,
    MessageTemplateForm,
  },
  props: {
    messageTemplates: Array,
    wa_endpoint: String,
  },
  data() {
    return {
      template_id: "",
      name: "",
      language: "en",
      category: "",
      header: {},
      body: {},
      footer: {},
      buttons: [],
      buttonType: "",
      status: "",
      headerExamples: {},
      bodyExamples: {},
      parameter_format: "POSITIONAL",
      authentication: {
        type: "copy_code",
        acceptTerms: false,
        apps: [{
          packageName: "",
          signatureHash: ""
        }],
        useSecurityRecommendation: false,
        useExpiryTime: false,
        expiryMinutes: 1,
        useCustomValidity: false,
        ttlMinutes: 10,
      },

      previewDialogVisible: false,
      addingMessageTemplate: false,
      previewHeader: {},
      previewBody: {},
      previewFooter: {},
      previewButtons: [],
      previewButtonType: "",
      previewingTemplate: false,

      updatingMessageTemplate: false,
      selectedMessageTemplateLanguage: "",
      selectedMessageTemplate: "",
      readOnly: false,

      refreshingMessageTemplate: false,
      addingNewLanguage: false,
      selectedLanguages: [],
      showRefreshButton: false,
    };
  },
  computed: {
    ...mapGetters(["showAdvanced"]),
    uniqueMessageTemplates() {
      return _.sortedUniqBy(this.messageTemplates, (o) => o.name) || [];
    },
    availableMessageTemplateLanguages() {
      return (
        _.map(
          _.filter(this.messageTemplates, (o) => o.name === this.$data.selectedMessageTemplate),
          (o) => o.language
        ) || []
      );
    },
    graphAPIVersion() {
      return this.$store.state.modules.whatsapp.graphAPIVersion || "v16.0";
    },
    categoryIsSupported() {
      const listOfSupportedCategories = messageTemplateCategories[this.graphAPIVersion]?.map(
        (item) => item.value
      );
      return !!this.category && listOfSupportedCategories.includes(this.category);
    },
  },
  mounted() {
    this.initEventListener();
  },
  beforeDestroy() {
    document.removeEventListener("keydown");
    document.removeEventListener("keyup");
  },
  methods: {
    initEventListener() {
      document.addEventListener("keydown", (event) => {
        if (event.altKey && event.shiftKey) {
          this.showRefreshButton = true;
        }
      });
      document.addEventListener("keyup", (event) => {
        this.showRefreshButton = false;
      });
    },
    async refreshTemplate() {
      this.refreshingMessageTemplate = true;
      await this.$store.dispatch("GET_WHATSAPP_MESSAGE_TEMPLATES", {
        data: { wa_endpoint: this.wa_endpoint },
        refresh: true,
      });
      this.$notify.success({
        title: "Success",
        position: "bottom-right",
        message: `Successfully refreshed message templates.`,
      });
      this.refreshingMessageTemplate = false;
    },
    addNewTemplate() {
      this.selectedMessageTemplate = "";
      this.selectedMessageTemplateLanguage = "";
      this.addingNewLanguage = false;
      this.selectedLanguages = [];
      this.clearCurrentMessageTemplateForm();
      this.$refs.form.$el.scrollIntoView({ behavior: "smooth" });
    },
    addNewLanguage() {
      this.addingNewLanguage = true;
      this.selectedLanguages = _.clone(this.availableMessageTemplateLanguages);
      this.selectedMessageTemplate = "";
      this.selectedMessageTemplateLanguage = "";
      this.language = "";
      this.header = {};
      this.body = {};
      this.footer = {};
      this.buttons = [];
      this.buttonType = "";
      this.readOnly = false;
      this.status = "";
      this.headerExamples = {};
      this.bodyExamples = {};
      this.$refs.form.$el.scrollIntoView({ behavior: "smooth" });
    },
    clearCurrentMessageTemplateForm() {
      this.name = "";
      this.language = "en";
      this.category = "";
      this.parameter_format = "POSITIONAL";
      this.header = {};
      this.body = {};
      this.footer = {};
      this.buttons = [];
      this.buttonType = "";
      this.readOnly = false;
      this.status = "";
      this.headerExamples = {};
      this.bodyExamples = {};
      this.authentication = {
        type: "copy_code",
        acceptTerms: false,
        apps: [{
          packageName: "",
          signatureHash: ""
        }],
        useSecurityRecommendation: false,
        useExpiryTime: false,
        expiryMinutes: 1,
        useCustomValidity: false,
        ttlMinutes: 10,
      }
    },
    deleteMessageTemplate() {
      this.$confirm(
        `You are about to delete message template ${this.selectedMessageTemplate}, including all language(s) that has been created, are you sure?`,
        "Warning",
        {
          confirmButtonText: "Confirm",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      )
        .then(() => {
          this.updatingMessageTemplate = true;
          this.$store
            .dispatch("DELETE_WHATSAPP_MESSAGE_TEMPLATES", {
              data: {
                name: this.selectedMessageTemplate,
                wa_endpoint: this.wa_endpoint,
              },
            })
            .then((res) => {
              this.$message({
                type: "success",
                message: `Message template successfully deleted!`,
              });
              this.clearCurrentMessageTemplateForm();
              this.updatingMessageTemplate = false;
            })
            .catch((err) => {
              this.$message({
                type: "error",
                message: `Failed to delete message template!`,
              });
              this.updatingMessageTemplate = false;
            });
        })
        .catch(() => {
          this.$message({
            type: "info",
            message: "Delete cancelled!",
          });
        });
    },
    changeMessageTemplate() {
      this.clearCurrentMessageTemplateForm();
      const messageTemplate = _.find(
        this.messageTemplates,
        (o) =>
          o.language === this.$data.selectedMessageTemplateLanguage &&
          o.name === this.$data.selectedMessageTemplate
      );
      if (messageTemplate) {
        // Track unmodified data to be used in update WHERE clause
        this.readOnly = true;
        const { id, name, category, language, status, components, parameter_format } = messageTemplate;
        const header = _.find(components, (component) => component.type === "HEADER");
        const body = _.find(components, (component) => component.type === "BODY");
        const footer = _.find(components, (component) => component.type === "FOOTER");

        const buttons = _.find(components, (component) => component.type === "BUTTONS");

        this.template_id = id;
        this.name = name;
        this.category = category;
        this.parameter_format = parameter_format || "POSITIONAL";
        this.language = language;

        if (category === "AUTHENTICATION") {
          this.authentication = {
            type: "copy_code",
            acceptTerms: false,
            apps: [{
              packageName: "",
              signatureHash: ""
            }],
            useSecurityRecommendation: false,
            useExpiryTime: false,
            expiryMinutes: 1,
            useCustomValidity: false,
            ttlMinutes: 10,
          }
          if (buttons?.buttons?.[0]?.url?.includes("COPY_CODE")) {
            this.authentication.type = "copy_code";
          } else if (buttons?.buttons?.[0]?.url?.includes("ZERO_TAP")) {
            this.authentication.type = "zero_tap";
            this.authentication.acceptTerms = true;
          } else {
            this.authentication.type = "one_tap";
          }

          // handling list of apps??
          const buttonUrl = buttons?.buttons?.[0]?.url;
          const paramsObj = new URLSearchParams(buttonUrl || "");
          const packageNameStr = paramsObj.get("package_name");
          const signatureHashStr = paramsObj.get("signature_hash");
          this.authentication.apps = packageNameStr?.split(",").map((packageName,index) => ({
            packageName,
            signatureHash: signatureHashStr?.split(",")[index],
          }));

          if (body?.add_security_recommendation) {
            this.authentication.useSecurityRecommendation = true;
          }

          if (footer?.code_expiration_minutes) {
            this.authentication.useExpiryTime = true;
            this.authentication.expiryMinutes = footer?.code_expiration_minutes;
          }

          if (messageTemplate?.message_send_ttl_seconds === 600) {
            this.authentication.useCustomValidity = false;
            this.authentication.ttlMinutes = 10;
          } else {
            this.authentication.useCustomValidity = true;
            this.authentication.ttlMinutes = messageTemplate?.message_send_ttl_seconds / 60;
          }
        }

        if (header) {
          if (header.format === "TEXT") {
            this.header = {
              type: "TEXT",
              text: header.text,
            };
            const examplesPositional = header?.example?.header_text || [];
            const examplesNamed = header?.example?.header_text_named_params || [];
            const examples = examplesPositional.length > 0 ? examplesPositional : examplesNamed;
            if (examples.length > 0) {
              const exampleTypePositional = typeof examples[0] === 'string';
              if (exampleTypePositional) {
                this.headerExamples[1] = examples[0];
              } else {
                this.headerExamples[examples[0].param_name] = examples[0].example;
              }
            } else {
              this.headerExamples = {};
            }
          } else {
            this.header = {
              type: "MEDIA",
              mediaType: header.format,
            };
            this.headerExamples = header?.example?.header_handle ? {
              "0": header?.example?.header_handle?.[0],
            } : {};
          }
        }

        if (body) {
          this.body = {
            text: body.text,
          };
          // this is for remapping for examples of POSITIONAL and NAMED parameters
          const examplesPositional = body?.example?.body_text?.[0] || [];
          const examplesNamed = body?.example?.body_text_named_params || [];
          const examples = examplesPositional.length > 0 ? examplesPositional : examplesNamed;
          examples.forEach((example, index) => {
            const exampleTypePositional = typeof example === 'string';
            if (exampleTypePositional) {
              this.bodyExamples[index + 1] = example;
            } else {
              this.bodyExamples[example.param_name] = example.example;
            }
          });
        }

        if (footer) {
          this.footer = {
            text: footer.text,
          };
        }

        if (buttons) {
          const buttonsArr = buttons.buttons;
          const isQuickReplies = _.every(buttonsArr, (button) => button.type === "QUICK_REPLY");
          if (isQuickReplies) {
            this.buttonType = "QUICK_REPLY";
          } else {
            this.buttonType = "CALL_TO_ACTION";
          }
          this.buttons = buttonsArr.map((button) => {
            if (button.type === "URL") {
              const urlButton = {
                ...button,
                example: button.example[0],
                url: button.url.replace(/{{(\d+)}}/, ""),
                urltype: /{{(\d+)}}/.test(button.url) ? "DYNAMIC" : "STATIC",
              };
              return urlButton;
            }
            return button;
          });
        }

        if (status) {
          this.status = status;
        }
      }
    },
    handleViewMessageTemplateChange(val) {
      this.selectedMessageTemplateLanguage = "";
      this.changeMessageTemplate();
    },
    handleViewMessageTemplateLanguageChange(val) {
      this.changeMessageTemplate();
    },

    getPreviewButtonType(messageTemplate) {
      if (messageTemplate.buttons.length === 0) return undefined;

      const quickReplyButtons = _.find(
        messageTemplate.buttons,
        (button) => button.type === "QUICK_REPLY"
      );
      if (quickReplyButtons) {
        return "QUICK_REPLY";
      }
      const callToActionButtons = _.find(
        messageTemplate.buttons,
        (button) => button.type === "PHONE_NUMBER" || button.type === "URL"
      );
      if (callToActionButtons) {
        return "CALL_TO_ACTION";
      }

      return undefined;
    },
    async preview(messageTemplate) {
      if (messageTemplate.category === "AUTHENTICATION") {
        this.previewingTemplate = true;
        const payload = {
          category: "AUTHENTICATION",
          language: messageTemplate?.language,
          add_security_recommendation: messageTemplate?.authentication?.useSecurityRecommendation,
          code_expiration_minutes: messageTemplate?.authentication?.useExpiryTime ? messageTemplate?.authentication?.expiryMinutes : undefined,
          button_types: "OTP"
        }
        try {
          const { data } = await this.$store.dispatch('PREVIEW_TEMPLATE', payload);
          const preview = data[0];
  
          this.previewButtonType = "CALL_TO_ACTION";
          this.previewButtons = preview?.buttons.map(button => ({
            type: messageTemplate?.authentication?.type?.toUpperCase(),
            text: messageTemplate?.authentication?.type === 'copy_code' ? button.text : button.autofill_text,
            url: "",
          }));
          this.previewBody = {
            text: preview?.body || "",
          };
          this.previewFooter = {
            text: preview?.footer || "",
          };
          this.previewingTemplate = false;
          this.previewDialogVisible = true;
          return;
        } catch (err) {
          this.$message({
            type: "error",
            message: "Failed to generate preview. Please try again later.",
          });
          this.previewingTemplate = false;
          this.previewDialogVisible = false;
          return;
        }
      };
      this.previewButtonType = this.getPreviewButtonType(messageTemplate);
      this.previewButtons = messageTemplate.buttons;
      this.previewBody = messageTemplate.body;
      this.previewFooter = messageTemplate.footer;
      this.previewHeader = {
        format:
          messageTemplate.header.type === "TEXT"
            ? messageTemplate.header.type
            : messageTemplate.header.mediaType,
        text: messageTemplate.header.text,
      };
      this.previewDialogVisible = true;
    },
    constructMessageTemplatePayload(messageTemplate) {
      if (messageTemplate.category === "AUTHENTICATION") {
        const { authentication } = messageTemplate;
        const messageTemplatePayload = {};
        if (authentication.useCustomValidity) {
          messageTemplatePayload.message_send_ttl_seconds = authentication.ttlMinutes * 60;
        }
        if (authentication.useSecurityRecommendation) {
          messageTemplatePayload.body = {
            type: "body",
            add_security_recommendation: authentication.useSecurityRecommendation
          };
        }
        if (authentication.useExpiryTime) {
          messageTemplatePayload.footer = {
            type: "footer",
            code_expiration_minutes: authentication.expiryMinutes
          };
        }
        if (authentication.type === "copy_code") {
          messageTemplatePayload.buttons = [
            {
              type: "otp",
              otp_type: "copy_code",
            }
          ];
        } else if (authentication.type === "one_tap") {
          messageTemplatePayload.buttons = [
            {
              type: "otp",
              otp_type: "one_tap",
              supported_apps: authentication.apps.map(app => ({
                package_name: app.packageName,
                signature_hash: app.signatureHash
              }))
            }
          ];
        } else if (authentication.type === "zero_tap") {
          messageTemplatePayload.buttons = [
            {
              type: "otp",
              otp_type: "zero_tap",
              zero_tap_terms_accepted: true,
              supported_apps: authentication.apps.map(app => ({
                package_name: app.packageName,
                signature_hash: app.signatureHash
              }))
            }
          ];
        }
        return messageTemplatePayload;
      }
      const messageTemplatePayload = {
        parameter_format: messageTemplate.parameterFormat || "POSITIONAL",
      };

      if (messageTemplate.header) {
        if (messageTemplate.header.type === "TEXT") {
          messageTemplatePayload.header = {
            mediaType: messageTemplate.header.type,
            text: messageTemplate.header.text,
          };

          if (messageTemplate.finalHeaderExamples.length > 0) {
            const header_text_key = (messageTemplate.parameterFormat || "POSITIONAL") === "POSITIONAL" ? "header_text" : "header_text_named_params";
            Object.assign(messageTemplatePayload.header, {
              example: {
                [header_text_key]: messageTemplate.finalHeaderExamples,
              },
            });
          }
        } else {
          if (
            messageTemplate.header.type === "MEDIA" &&
            messageTemplate.finalHeaderExamples.length === 0
          ) {
            this.$message({
              type: "error",
              message: "Media sample need to be provided",
            });
            this.addingMessageTemplate = false;
            return;
          }
          messageTemplatePayload.header = {
            mediaType: messageTemplate.header.mediaType,
          };

          if (messageTemplate.finalHeaderExamples.length > 0) {
            Object.assign(messageTemplatePayload.header, {
              example: {
                header_handle: messageTemplate.finalHeaderExamples,
              },
            });
          }
        }
      }

      if (messageTemplate.body && messageTemplate.body.text) {
        messageTemplatePayload.body = messageTemplate.body;

        if (messageTemplate.finalBodyExamples.length > 0) {
          const body_text_key = (messageTemplate.parameterFormat || "POSITIONAL") === "POSITIONAL"
            ? "body_text"
            : "body_text_named_params";
          const examples = (messageTemplate.parameterFormat || "POSITIONAL") === "POSITIONAL"
            ? [messageTemplate.finalBodyExamples] // it is multi dimensional array as Graph API doc
            : messageTemplate.finalBodyExamples
          Object.assign(messageTemplatePayload.body, {
            example: {
              [body_text_key]: examples,
            },
          });
        }
      }

      if (messageTemplate.footer && messageTemplate.footer.text) {
        messageTemplatePayload.footer = messageTemplate.footer;
      }

      if (Array.isArray(this.buttons) && this.buttons.length > 0) {
        messageTemplatePayload.buttons = this.buttons.map((button) => {
          if (button.type === "PHONE_NUMBER") {
            const phoneButton = {
              ...button,
              phone_number: `${button.country_code}${button.phone_number}`,
            };
            delete phoneButton.country_code;
            delete phoneButton.url;
            delete phoneButton.urltype;
            return phoneButton;
          } else if (button.type === "URL") {
            const urlButton = {
              ...button,
              url: button.urltype === "STATIC" ? button.url : button.url + "{{1}}",
              example: [button.example],
            };
            delete urlButton.country_code;
            delete urlButton.urltype;
            delete urlButton.phone_number;
            return urlButton;
          }
          return button;
        });
      }

      return messageTemplatePayload;
    },
    confirm(action, messageTemplate) {
      this.$confirm("Do you confirm to add this message template?", "Confirm", {
        confirmButtonText: "Confirm",
        cancelButtonText: "Preview First",
        type: "info",
      })
      .then(() => {
        if (action === "create") {
          this.addMessageTemplate(messageTemplate);
        } else {
          this.updateMessageTemplate(messageTemplate);
        }
      })
      .catch((err) => {
        this.preview(messageTemplate);
      });
    },
    addMessageTemplate(messageTemplate) {
      this.addingMessageTemplate = true;

      const messageTemplatePayload = this.constructMessageTemplatePayload(messageTemplate);

      this.$store
        .dispatch("ADD_WHATSAPP_MESSAGE_TEMPLATES", {
          data: {
            ...messageTemplatePayload,
            name: messageTemplate.templateName,
            category: messageTemplate.category,
            language: messageTemplate.language,
            wa_endpoint: this.wa_endpoint,
          },
        })
        .then((res) => {
          this.$message({
            type: "success",
            message: res,
          });
          this.addingMessageTemplate = false;
        })
        .catch((err) => {
          this.$message({
            type: "error",
            message: err?.response?.data?.error?.message || "Internal Server Error",
          });
          this.addingMessageTemplate = false;
        });
    },
    updateMessageTemplate(messageTemplate) {
      this.updatingMessageTemplate = true;
      const messageTemplatePayload = this.constructMessageTemplatePayload(messageTemplate);
      this.$store
        .dispatch("UPDATE_WHATSAPP_MESSAGE_TEMPLATES", {
          data: {
            ...messageTemplatePayload,
            name: messageTemplate.templateName,
            language: messageTemplate.language,
            template_id: this.template_id,
          },
        })
        .then((res) => {
          this.$message({
            type: "success",
            message: res,
          });
          this.updatingMessageTemplate = false;
        })
        .catch((err) => {
          this.$message({
            type: "error",
            message: err?.response?.data?.error?.message || "Internal Server Error",
          });
          this.updatingMessageTemplate = false;
        });
    },
  },
};
</script>
